import React, { useEffect, useState, useCallback } from 'react';
import { Link } from 'react-router-dom';
import { Search } from 'lucide-react';
import apiClient from '../../utils/api';

const STATUS_OPTIONS = ['', 'PENDING', 'COMPLETED', 'FAILED', 'REFUNDED'];
const LIMIT = 20;

const paymentShipment = (payment: any) => payment.Shipment ?? payment.shipment ?? null;

const firstText = (...values: any[]) =>
  values.find(value => typeof value === 'string' && value.trim())?.trim();

const badgeClass = (status?: string) => `badge badge-${status ? status.toLowerCase().replaceAll('_', '-') : 'inactive'}`;

const paymentDestination = (payment: any) => {
  const shipment = paymentShipment(payment);
  const latestTracking = Array.isArray(shipment?.tracking) ? shipment.tracking[0] : null;
  const deliveryLocation = firstText(
    shipment?.deliveryLocation,
    payment.deliveryLocation,
    shipment?.receiverCity,
    shipment?.receiverAddress,
    shipment?.departmentBranch?.city,
    shipment?.departmentBranch?.address,
    shipment?.departmentBranch?.name ? `Branch: ${shipment.departmentBranch.name}` : null,
  );
  const details = [
    shipment?.receiverAddress,
    shipment?.departmentBranch?.name ? `Branch: ${shipment.departmentBranch.name}` : null,
  ].filter(Boolean);
  const currentLocation = firstText(shipment?.currentLocation, payment.currentLocation, latestTracking?.location);

  return {
    label: deliveryLocation || '—',
    details: details.filter(part => part !== deliveryLocation).join(', '),
    location: currentLocation && currentLocation !== deliveryLocation ? currentLocation : null,
  };
};

export const SuperAdminPayments: React.FC = () => {
  const [payments, setPayments] = useState<any[]>([]);
  const [companies, setCompanies] = useState<any[]>([]);
  const [total, setTotal] = useState(0);
  const [page, setPage] = useState(0);
  const [statusFilter, setStatusFilter] = useState('');
  const [companyFilter, setCompanyFilter] = useState('');
  const [search, setSearch] = useState('');
  const [dateFrom, setDateFrom] = useState('');
  const [dateTo, setDateTo] = useState('');
  const [loading, setLoading] = useState(true);
  const [actionLoading, setActionLoading] = useState('');
  const [error, setError] = useState('');

  useEffect(() => {
    apiClient.listCompanies(200, 0).then(r => {
      const d = r.data.data;
      setCompanies(d?.companies ?? d?.rows ?? (Array.isArray(d) ? d : []));
    }).catch(() => {});
  }, []);

  const load = useCallback(async () => {
    setLoading(true);
    setError('');
    try {
      const filters: any = {};
      if (statusFilter) filters.status = statusFilter;
      if (companyFilter) filters.companyId = companyFilter;
      if (search) filters.search = search;
      if (dateFrom) filters.dateFrom = dateFrom;
      if (dateTo) filters.dateTo = dateTo;
      const res = await apiClient.listPayments(filters, LIMIT, page * LIMIT);
      const d = res.data.data;
      setPayments(d?.payments ?? d?.rows ?? (Array.isArray(d) ? d : []));
      setTotal(d?.total ?? d?.count ?? 0);
    } catch (e: any) {
      setPayments([]);
      setTotal(0);
      setError(e.response?.data?.error || 'Failed to load payments. Please refresh or check the API server.');
    } finally { setLoading(false); }
  }, [page, statusFilter, companyFilter, search, dateFrom, dateTo]);

  useEffect(() => { load(); }, [load]);

  const complete = async (id: string) => {
    setActionLoading(id);
    try { await apiClient.completePayment(id, {}); load(); } catch {} finally { setActionLoading(''); }
  };

  const fail = async (id: string) => {
    const reason = globalThis.prompt('Reason for failing this payment:') ?? 'Marked failed by admin';
    setActionLoading(id);
    try { await apiClient.failPayment(id, reason); load(); } catch {} finally { setActionLoading(''); }
  };

  const refund = async (id: string) => {
    const reason = globalThis.prompt('Reason for refund:') ?? 'Refunded by admin';
    setActionLoading(id);
    try { await apiClient.refundPayment(id, reason); load(); } catch {} finally { setActionLoading(''); }
  };

  const totalPages = Math.ceil(total / LIMIT);

  return (
    <>
      <div className="page-header">
        <h1 className="page-title">All Payments <span style={{ fontSize: 14, color: '#94a3b8', fontWeight: 400 }}>({total.toLocaleString()})</span></h1>
      </div>

      <div className="card">
        <div style={{ display: 'flex', gap: 10, marginBottom: 16, flexWrap: 'wrap' }}>
          <div style={{ position: 'relative', flex: 1, minWidth: 180 }}>
            <Search size={15} style={{ position: 'absolute', left: 10, top: '50%', transform: 'translateY(-50%)', color: '#94a3b8' }} />
            <input placeholder="Search tracking, receiver, phone, address, reference..." value={search} onChange={e => { setSearch(e.target.value); setPage(0); }} style={{ paddingLeft: 32, width: '100%' }} />
          </div>
          <select value={statusFilter} onChange={e => { setStatusFilter(e.target.value); setPage(0); }} style={{ width: 160 }}>
            {STATUS_OPTIONS.map(s => <option key={s} value={s}>{s || 'All Statuses'}</option>)}
          </select>
          <select value={companyFilter} onChange={e => { setCompanyFilter(e.target.value); setPage(0); }} style={{ width: 180 }}>
            <option value="">All Companies</option>
            {companies.map(c => <option key={c.id} value={c.id}>{c.name}</option>)}
          </select>
          <input type="date" value={dateFrom} onChange={e => { setDateFrom(e.target.value); setPage(0); }} title="From date" style={{ width: 140 }} />
          <input type="date" value={dateTo} onChange={e => { setDateTo(e.target.value); setPage(0); }} title="To date" style={{ width: 140 }} />
          <button className="btn btn-ghost" onClick={() => { setSearch(''); setStatusFilter(''); setCompanyFilter(''); setDateFrom(''); setDateTo(''); setPage(0); }}>Clear</button>
        </div>

        {error && (
          <div style={{ background: '#fee2e2', color: '#991b1b', padding: '10px 14px', borderRadius: 6, marginBottom: 12 }}>
            {error}
          </div>
        )}

        {loading ? <div className="loading-spinner">Loading payments...</div> : error ? null : payments.length === 0 ? (
          <div className="empty-state"><h3>No payments found</h3></div>
        ) : (
          <>
            <div className="table-wrapper">
              <table>
                <thead>
                  <tr><th>Tracking #</th><th>Sender</th><th>Receiver</th><th>Destination</th><th>Company</th><th>Shipment Status</th><th>Amount</th><th>Payment</th><th>Method</th><th>Date</th><th>Actions</th></tr>
                </thead>
                <tbody>
                  {payments.map((p: any) => {
                    const shipment = paymentShipment(p);
                    const destination = paymentDestination(p);
                    const amount = Number(shipment?.totalAmount ?? p.amount ?? 0);
                    const currency = shipment?.currency ?? p.currency ?? 'RWF';
                    const shipmentDate = shipment?.createdAt ?? p.createdAt;
                    const shipmentId = shipment?.id ?? p.shipmentId;
                    return (
                      <tr key={p.id}>
                        <td>
                          <code style={{ fontSize: 11 }}>{shipment?.trackingNumber ?? p.shipmentId}</code>
                          {shipment?.references && <div style={{ fontSize: 11, color: '#94a3b8', marginTop: 2 }}>Ref: {shipment.references}</div>}
                        </td>
                        <td>{shipment?.senderName || '—'}</td>
                        <td>
                          <div style={{ fontWeight: 500 }}>{shipment?.receiverName || '—'}</div>
                          <div style={{ fontSize: 12, color: '#64748b', marginTop: 2 }}>
                            Phone: {shipment?.receiverPhone || '—'}
                          </div>
                        </td>
                        <td style={{ minWidth: 220 }}>
                          <div style={{ fontSize: 12, color: '#334155' }}>{destination.label}</div>
                          {destination.details && (
                            <div style={{ fontSize: 11, color: '#64748b', marginTop: 2 }}>{destination.details}</div>
                          )}
                          {destination.location && destination.location !== destination.label && (
                            <div style={{ fontSize: 11, color: '#94a3b8', marginTop: 2 }}>Current: {destination.location}</div>
                          )}
                        </td>
                        <td style={{ fontSize: 12, color: '#64748b' }}>{shipment?.company?.name ?? p.company?.name ?? '—'}</td>
                        <td><span className={badgeClass(shipment?.status)}>{shipment?.status || '—'}</span></td>
                        <td style={{ whiteSpace: 'nowrap' }}>{amount.toLocaleString()} {currency}</td>
                        <td>
                          <span className={badgeClass(p.status)}>{p.status}</span>
                          {p.dueDate && <div style={{ fontSize: 11, color: '#64748b', marginTop: 2 }}>Due: {new Date(p.dueDate).toLocaleDateString()}</div>}
                          {p.paidAt && <div style={{ fontSize: 11, color: '#64748b', marginTop: 2 }}>Paid: {new Date(p.paidAt).toLocaleDateString()}</div>}
                          {p.notes && <div style={{ fontSize: 11, color: '#94a3b8', marginTop: 2, maxWidth: 160, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }} title={p.notes}>{p.notes}</div>}
                        </td>
                        <td>{p.paymentMethod || '—'}</td>
                        <td style={{ whiteSpace: 'nowrap', fontSize: 12 }}>{shipmentDate ? new Date(shipmentDate).toLocaleDateString() : '—'}</td>
                        <td style={{ display: 'flex', gap: 6, flexWrap: 'wrap' }}>
                          {shipmentId && <Link to={`/super-admin/shipments/${shipmentId}`} className="btn btn-ghost" style={{ padding: '3px 8px', fontSize: 12 }}>View</Link>}
                          {p.status === 'PENDING' && !p.isMissingPayment && (
                            <>
                              <button className="btn btn-success" style={{ padding: '3px 8px', fontSize: 12 }} disabled={actionLoading === p.id} onClick={() => complete(p.id)}>Complete</button>
                              <button className="btn btn-danger" style={{ padding: '3px 8px', fontSize: 12 }} disabled={actionLoading === p.id} onClick={() => fail(p.id)}>Fail</button>
                            </>
                          )}
                          {p.status === 'COMPLETED' && !p.isMissingPayment && (
                            <button className="btn btn-ghost" style={{ padding: '3px 8px', fontSize: 12 }} disabled={actionLoading === p.id} onClick={() => refund(p.id)}>Refund</button>
                          )}
                        </td>
                      </tr>
                    );
                  })}
                </tbody>
              </table>
            </div>
            {totalPages > 1 && (
              <div className="pagination">
                <button className="btn btn-ghost" onClick={() => setPage(p => p - 1)} disabled={page === 0}>Prev</button>
                <span>Page {page + 1} of {totalPages}</span>
                <button className="btn btn-ghost" onClick={() => setPage(p => p + 1)} disabled={page + 1 >= totalPages}>Next</button>
              </div>
            )}
          </>
        )}
      </div>
    </>
  );
};
